import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import {
	downloadFile,
	Editor,
	serializeTldrawJsonBlob,
	TLDRAW_FILE_EXTENSION,
	TLUiOverrides,
	useTranslation,
} from 'tldraw'
import { routes } from '../../../routeDefs'
import { useHandleUiEvents } from '../../../utils/analytics'
import { TldrawApp } from '../../app/TldrawApp'
import { useMaybeApp } from '../../hooks/useAppState'
import { editorMessages as messages } from './editor-messages'
import { useRoomInfo } from './TlaEditorTopRightPanel'

/** Triggers a GET to the app file download endpoint; browser shows download immediately with progress. */
export function downloadAppFile(fileId: string) {
	const url = `/api/app/file/${fileId}/download`
	const a = document.createElement('a')
	a.href = url
	// meaningful file name is set by Content-Disposition header
	a.download = ''
	a.click()
}

export async function downloadFileFromEditor(editor: Editor, name: string) {
	const blobToSave = await serializeTldrawJsonBlob(editor)
	const file = new File([blobToSave], name, { type: 'application/json' })
	downloadFile(file)
}

/** The app file record's name when there is one, else the document's, else the untitled fallback
 *  (rather than the date the sidebar would show). `||` not `??`: getFileName returns '' for a file
 *  the app doesn't know yet (first visit to a shared link), which must fall through to the
 *  document name like the top-left panel does. */
export function getEditorFileName(
	app: TldrawApp | null,
	fileSlug: string | undefined,
	editor: Editor | null,
	untitled: string
) {
	return (
		(fileSlug ? app?.getFileName(fileSlug, false)?.trim() : null) ||
		editor?.getDocumentSettings().name ||
		untitled
	)
}

export function useFileEditorOverrides({ fileSlug }: { fileSlug?: string }) {
	const msg = useTranslation()
	const app = useMaybeApp()
	const untitledProject = msg(messages.untitledProject)
	const intl = useIntl()
	const navigate = useNavigate()
	const trackEvent = useHandleUiEvents()
	// Built from the route params rather than the raw pathname: a trailing slash (`/r/abc/`, which
	// the router still matches) would otherwise produce a createSource the room can never resolve.
	const roomInfo = useRoomInfo()
	const copySource = roomInfo ? `${roomInfo.prefix}/${roomInfo.id}` : null

	const overrides = useMemo<TLUiOverrides>(() => {
		return {
			translations: {
				en: {
					'people-menu.anonymous-user': intl.formatMessage(messages.anonymousUser),
				},
			},
			actions(editor, actions) {
				// Add a shortcut that does nothing but blocks the command+s shortcut
				actions['save-null'] = {
					id: 'save-null',
					label: 'action.save-copy',
					readonlyOk: true,
					kbd: 'cmd+s,ctrl+s',
					onSelect() {
						trackEvent('save-project-no-action', { source: 'kbd' })
					},
				}
				actions['save-file-copy'] = {
					id: 'save-file-copy',
					label: intl.formatMessage(messages.downloadFile),
					readonlyOk: true,
					async onSelect() {
						trackEvent('download-file', { source: '' })
						if (app && fileSlug) {
							downloadAppFile(fileSlug)
						} else {
							const defaultName =
								getEditorFileName(app, fileSlug, editor, untitledProject) + TLDRAW_FILE_EXTENSION
							await downloadFileFromEditor(editor, defaultName)
						}
					},
				}

				actions['copy-to-my-files'] = {
					id: 'copy-to-my-files',
					label: intl.formatMessage(messages.copyToMyfiles),
					readonlyOk: true,
					async onSelect() {
						if (!copySource) return
						const defaultName = getEditorFileName(app, fileSlug, editor, untitledProject)
						const res = await app?.createFile({
							name: defaultName,
							createSource: copySource,
						})
						if (res?.ok) {
							const { fileId } = res.value
							navigate(routes.tlaFile(fileId))
							trackEvent('create-file', { source: 'legacy-import-button' })
						}
					},
				}

				return actions
			},
		}
	}, [app, copySource, fileSlug, untitledProject, intl, navigate, trackEvent])

	return overrides
}
